import { TourTypeSelect, parseTourType, type TourType } from '@/components/travel/direction';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

const difficultyOptions = [
  { value: 'EASY', label: 'Easy' },
  { value: 'MODERATE', label: 'Moderate' },
  { value: 'CHALLENGING', label: 'Challenging' },
  { value: 'DIFFICULT', label: 'Difficult' },
] as const;

const statusOptions = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

type Difficulty = typeof difficultyOptions[number]['value'];
type Status = typeof statusOptions[number]['value'];

interface PackageFormData {
  code: string;
  name: string;
  slug: string;
  shortDescription?: string;
  description?: string;
  duration: number;
  destinations: string;
  categoryId?: string;
  tags?: string;
  difficulty: Difficulty;
  status?: Status;
  isFeatured?: boolean;
  cancellationPolicy?: string;
}

interface PackageModalProps {
  defaultTourType?: TourType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  package?: {
    tourType?: TourType | null;
    id: string;
    code: string;
    name: string;
    slug: string;
    shortDescription?: string | null;
    description?: string | null;
    duration: number;
    destinations: string[];
    categoryId?: string | null;
    tags: string[];
    difficulty: Difficulty;
    status: Status;
    isFeatured: boolean;
    cancellationPolicy?: string | null;
  };
  onSuccess?: () => void;
}

export function PackageModal({ open, onOpenChange, package: pkg, onSuccess, defaultTourType }: PackageModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!pkg;
  const [tourType, setTourType] = useState<TourType | undefined>(undefined);
  useEffect(() => { if(open) setTourType(pkg ? pkg.tourType ?? undefined : defaultTourType); }, [open, pkg, defaultTourType]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<PackageFormData>({
    defaultValues: pkg
      ? {
          code: pkg.code,
          name: pkg.name,
          slug: pkg.slug,
          shortDescription: pkg.shortDescription ?? undefined,
          description: pkg.description ?? undefined,
          duration: pkg.duration,
          destinations: pkg.destinations.join(', '),
          categoryId: pkg.categoryId ?? undefined,
          tags: pkg.tags.join(', '),
          difficulty: pkg.difficulty,
          status: pkg.status,
          isFeatured: pkg.isFeatured,
          cancellationPolicy: pkg.cancellationPolicy ?? undefined,
        }
      : {
          difficulty: 'MODERATE',
          status: 'DRAFT',
          duration: 1,
          isFeatured: false,
        },
  });

  // Reset form when package changes or modal opens
  useEffect(() => {
    if (open) {
      if (pkg) {
        reset({
          code: pkg.code,
          name: pkg.name,
          slug: pkg.slug,
          shortDescription: pkg.shortDescription ?? undefined,
          description: pkg.description ?? undefined,
          duration: pkg.duration,
          destinations: pkg.destinations.join(', '),
          categoryId: pkg.categoryId ?? undefined,
          tags: pkg.tags.join(', '),
          difficulty: pkg.difficulty,
          status: pkg.status,
          isFeatured: pkg.isFeatured,
          cancellationPolicy: pkg.cancellationPolicy ?? undefined,
        });
      } else {
        reset({
          difficulty: 'MODERATE',
          status: 'DRAFT',
          duration: 1,
          isFeatured: false,
          code: '',
          name: '',
          slug: '',
          destinations: '',
        });
      }
    }
  }, [open, pkg, reset]);

  const { data: categories } = trpc.package.categories.useQuery();
  const utils = trpc.useUtils();

  const createMutation = trpc.package.create.useMutation({
    onSuccess: () => {
      utils.package.list.invalidate();
      reset();
      onOpenChange(false);
      onSuccess?.();
      showSuccessToast('Package created successfully');
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || 'Failed to create package');
    },
  });

  const updateMutation = trpc.package.update.useMutation({
    onSuccess: () => {
      utils.package.list.invalidate();
      utils.package.byId.invalidate();
      onOpenChange(false);
      onSuccess?.();
      showSuccessToast('Package updated successfully');
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || 'Failed to update package');
    },
  });

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const onSubmit = (data: PackageFormData) => {
    setIsSubmitting(true);

    const destinations = data.destinations
      .split(',')
      .map((d) => d.trim())
      .filter(Boolean);

    const tags = data.tags
      ? data.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      : [];

    if (isEditing && pkg) {
      updateMutation.mutate({
        id: pkg.id,
        data: {
          tourType: tourType ?? null,
          code: data.code,
          name: data.name,
          slug: data.slug,
          shortDescription: data.shortDescription,
          description: data.description,
          duration: data.duration,
          destinations,
          categoryId: data.categoryId || null,
          tags,
          difficulty: data.difficulty,
          status: data.status,
          isFeatured: data.isFeatured,
          cancellationPolicy: data.cancellationPolicy || null,
        },
      });
    } else {
      createMutation.mutate({
        tourType: tourType ?? null,
        code: data.code,
        name: data.name,
        slug: data.slug,
        shortDescription: data.shortDescription,
        description: data.description,
        duration: data.duration,
        destinations,
        categoryId: data.categoryId,
        tags,
        difficulty: data.difficulty,
        isFeatured: data.isFeatured,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Package' : 'Create New Package'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the package information below.'
              : 'Fill in the package details to create a new travel package.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <TourTypeSelect value={tourType} onChange={value => setTourType(parseTourType(value))} />
          {/* Basic Info */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground">Basic Information</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="code">Package Code *</Label>
                <Input
                  id="code"
                  {...register('code', { required: 'Package code is required' })}
                  placeholder="PKG-001"
                />
                {errors.code && (
                  <p className="text-sm text-red-500">{errors.code.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Package Name *</Label>
                <Input
                  id="name"
                  {...register('name', { required: 'Package name is required' })}
                  placeholder="Amazing Turkey Tour"
                  onBlur={(e) => {
                    if (!watch('slug') && e.target.value) {
                      setValue('slug', generateSlug(e.target.value));
                    }
                  }}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">URL Slug *</Label>
              <Input
                id="slug"
                {...register('slug', { required: 'URL slug is required' })}
                placeholder="amazing-turkey-tour"
              />
              {errors.slug && (
                <p className="text-sm text-red-500">{errors.slug.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="shortDescription">Short Description</Label>
              <Input
                id="shortDescription"
                {...register('shortDescription')}
                placeholder="A brief summary of the package"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Full Description</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Detailed description of the travel package..."
                rows={4}
              />
            </div>
          </div>

          {/* Tour Details */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground">Tour Details</h4>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (days) *</Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  {...register('duration', {
                    required: 'Duration is required',
                    valueAsNumber: true,
                    min: { value: 1, message: 'Duration must be at least 1 day' },
                  })}
                  placeholder="7"
                />
                {errors.duration && (
                  <p className="text-sm text-red-500">{errors.duration.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Difficulty</Label>
                <Combobox
                  options={difficultyOptions.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  }))}
                  value={watch('difficulty') || 'MODERATE'}
                  onValueChange={(value) => setValue('difficulty', value as Difficulty)}
                  placeholder="Select difficulty"
                  searchPlaceholder="Search..."
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Combobox
                  options={[
                    { value: 'none', label: 'No Category' },
                    ...(categories?.filter((cat) => cat.id).map((cat) => ({
                      value: cat.id,
                      label: cat.name,
                    })) ?? []),
                  ]}
                  value={watch('categoryId') ?? 'none'}
                  onValueChange={(value) => setValue('categoryId', value === 'none' ? undefined : value)}
                  placeholder="Select category"
                  searchPlaceholder="Search categories..."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="destinations">Destinations *</Label>
              <Input
                id="destinations"
                {...register('destinations', { required: 'At least one destination is required' })}
                placeholder="Istanbul, Cappadocia, Antalya (comma-separated)"
              />
              {errors.destinations && (
                <p className="text-sm text-red-500">{errors.destinations.message}</p>
              )}
              <p className="text-xs text-muted-foreground">Separate multiple destinations with commas</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tags">Tags</Label>
              <Input
                id="tags"
                {...register('tags')}
                placeholder="adventure, cultural, family-friendly (comma-separated)"
              />
              <p className="text-xs text-muted-foreground">Separate multiple tags with commas</p>
            </div>
          </div>

          {/* Policies */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground">Policies</h4>
            <div className="space-y-2">
              <Label htmlFor="cancellationPolicy">Cancellation Policy</Label>
              <Textarea
                id="cancellationPolicy"
                {...register('cancellationPolicy')}
                placeholder="Enter your cancellation and refund policy. E.g., 'Free cancellation up to 30 days before departure. 50% refund for cancellations between 15-30 days. No refund for cancellations within 14 days.'"
                rows={4}
              />
              <p className="text-xs text-muted-foreground">This will be displayed to customers before booking</p>
            </div>
          </div>

          {/* Status & Options */}
          {isEditing && (
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground">Status & Options</h4>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Combobox
                    options={statusOptions.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))}
                    value={watch('status') || 'DRAFT'}
                    onValueChange={(value) => setValue('status', value as Status)}
                    placeholder="Select status"
                    searchPlaceholder="Search..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Featured Package</Label>
                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      id="isFeatured"
                      {...register('isFeatured')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="isFeatured" className="font-normal">
                      Show on homepage as featured
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Messages */}
          {(createMutation.error || updateMutation.error) && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {createMutation.error?.message || updateMutation.error?.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Create Package'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
